import { createContext, useState, useEffect, useContext, useRef } from "react";
import * as Sentry from "@sentry/react";
import api from "../api/client";
import { clearFilterStorage } from "../utils/filterStorage";
import axios from "axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);
    const fetchingRef = useRef(false);

    useEffect(() => {
        // Solo intentar obtener usuario si no estamos en la página de login
        if (!window.location.pathname.includes('/login')) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchUser = async () => {
        // Evitar múltiples llamadas simultáneas
        if (fetchingRef.current) return;
        
        fetchingRef.current = true;
        try {
            // Obtener CSRF cookie primero
            await axios.get(`${import.meta.env.VITE_BACKEND_URL}/sanctum/csrf-cookie`, {
                withCredentials: true,
            });

            const response = await api.get("/user");
            setUser(response.data.user);
            setPermissions(response.data.permissions);
        } catch (error) {
            // 401 is expected when the user is not authenticated — do not report to Sentry
            if (error?.response?.status !== 401) {
                Sentry.captureException(error);
            }
            setUser(null);
            setPermissions(null);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    };

    const login = async (email, password, remember = false) => {
        // Obtener CSRF cookie primero
        await axios.get(`${import.meta.env.VITE_BACKEND_URL}/sanctum/csrf-cookie`, {
            withCredentials: true,
        });

        const response = await axios.post(
            `${import.meta.env.VITE_BACKEND_URL}/api/login`,
            { email, password, remember },
            { withCredentials: true }
        );
        clearFilterStorage();
        setUser(response.data.user);
        setPermissions(response.data.permissions);
        return response.data;
    };

    const logout = async () => {
        try {
            await axios.post(
                `${import.meta.env.VITE_BACKEND_URL}/api/logout`,
                null,
                { withCredentials: true }
            );
        } finally {
            clearFilterStorage();
            setUser(null);
            setPermissions(null);
        }
    };

    return (
        <AuthContext.Provider
        value={{ user, permissions, loading, login, logout, refetch: fetchUser }}
        >
        {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth debe usarse dentro de AuthProvider");
    }
    return context;
};
